import { ColumnDef } from "@tanstack/react-table"
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
    createRestaurantMenu,
    deleteRestaurantMenu,
    IListRestaurantMenusResponse,
    IRestaurantMenu,
    IRestaurantMenuAdminContext,
    IUpdateRestaurantMenuPayload,
    listRestaurantMenus,
    updateRestaurantMenu,
} from "@/shared/api/restaurant-menus"
import ArrowRightSvg from "@/shared/assets/images/ArrowRight.svg"
import PencilSvg from "@/shared/assets/images/Pencil.svg"
import TrashSvg from "@/shared/assets/images/Trash.svg"
import notify from "@/shared/lib/notify"
import { ActionButton } from "@/shared/ui/action-button"
import { ConfirmModal } from "@/shared/ui/confirm-modal"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { Table } from "@/shared/ui/table"
import { PageHeader } from "@/widgets/page-header"
import { getApiErrorMessage } from "../lib/get-api-error-message"
import { RestaurantMenuFormValues } from "../model/types"
import { RestaurantMenuModal } from "./restaurant-menu-modal"

const unavailableText = "Для этого сообщества управление меню недоступно. Обратитесь к General Admin"
const limitTooltip = "Достигнут лимит меню для сообщества. Изменить лимит можно в General Admin"

const emptyContext: IRestaurantMenuAdminContext = {
    menu_count: 0,
    max_menu_count: 1,
    external_menus: [],
    organizations: [],
    terminal_groups: [],
    order_types: [],
}

const formatDate = (value: string | null): string => {
    if (!value) {
        return "—"
    }

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
        return "—"
    }

    return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date)
}

const normalizeText = (value: string): string => value.trim()

const buildPayload = (values: RestaurantMenuFormValues): IUpdateRestaurantMenuPayload => {
    return {
        name: normalizeText(values.name),
        api_login: normalizeText(values.api_login),
        organization_id: normalizeText(values.organization_id),
        external_menu_id: normalizeText(values.external_menu_id),
        iiko_config: {
            terminal_group_id: normalizeText(values.terminal_group_id),
            default_order_type_id: normalizeText(values.default_order_type_id),
        },
    }
}

export const RestaurantMenusPage = () => {
    const navigate = useNavigate()
    const [data, setData] = useState<IListRestaurantMenusResponse | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [listError, setListError] = useState<string | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [currentMenu, setCurrentMenu] = useState<IRestaurantMenu | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [modalError, setModalError] = useState<string | null>(null)
    const [menuToDelete, setMenuToDelete] = useState<IRestaurantMenu | null>(null)

    const menus = useMemo(() => data?.menus ?? [], [data])
    const context = data?.context ?? emptyContext
    const isUnavailable = Boolean(data && context.max_menu_count === 0)
    const isLimitReached = context.menu_count >= context.max_menu_count

    const fetchMenus = useCallback(async () => {
        try {
            setIsLoading(true)
            setListError(null)
            const response = await listRestaurantMenus()
            setData(response.data)
        } catch (error) {
            setData(null)
            setListError(getApiErrorMessage(error))
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchMenus()
    }, [fetchMenus])

    const openCreateModal = () => {
        setCurrentMenu(null)
        setModalError(null)
        setIsModalOpen(true)
    }

    const openEditModal = (menu: IRestaurantMenu) => {
        setCurrentMenu(menu)
        setModalError(null)
        setIsModalOpen(true)
    }

    const closeModal = () => {
        if (isSubmitting) {
            return
        }

        setIsModalOpen(false)
        setCurrentMenu(null)
        setModalError(null)
    }

    const closeModalAfterSuccess = () => {
        setIsModalOpen(false)
        setCurrentMenu(null)
        setModalError(null)
    }

    const handleSubmit = async (values: RestaurantMenuFormValues) => {
        const payload = buildPayload(values)

        try {
            setIsSubmitting(true)
            setModalError(null)

            if (currentMenu?.id) {
                await updateRestaurantMenu(currentMenu.id, payload)
                notify.success("Меню успешно обновлено")
            } else {
                await createRestaurantMenu({
                    name: payload.name ?? "",
                    api_login: payload.api_login ?? "",
                    organization_id: payload.organization_id ?? "",
                    iiko_config: payload.iiko_config,
                    ...(payload.external_menu_id ? { external_menu_id: payload.external_menu_id } : {}),
                })
                notify.success("Меню успешно создано")
            }

            closeModalAfterSuccess()
            fetchMenus()
        } catch (error) {
            setModalError(getApiErrorMessage(error))
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleConfirmDelete = async () => {
        if (!menuToDelete?.id) {
            return
        }

        try {
            await deleteRestaurantMenu(menuToDelete.id)
            notify.success("Меню удалено")
            setMenuToDelete(null)
            fetchMenus()
        } catch (error) {
            notify.error(getApiErrorMessage(error))
        }
    }

    const columns: ColumnDef<IRestaurantMenu, ReactNode>[] = [
        {
            accessorFn: (menu) => (
                <div className="flex flex-col">
                    <span className="font-medium whitespace-normal">{menu.name || "—"}</span>
                    <span className="text-sm text-[#828282]">{menu.id}</span>
                </div>
            ),
            id: "name",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Название</div>,
        },
        {
            accessorFn: (menu) => <div>{menu.slug || "—"}</div>,
            id: "slug",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Slug</div>,
        },
        {
            accessorFn: (menu) => menu.external_menu_id ? (
                <div>{menu.external_menu_id}</div>
            ) : (
                <span className="inline-flex w-fit rounded-full bg-[#F7F8FA] px-3 py-1 text-sm text-[#4F4F4F]">
                    поиск по имени
                </span>
            ),
            id: "external_menu_id",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">external_menu_id</div>,
        },
        {
            accessorFn: (menu) => (
                <div title={menu.revision ? `revision: ${menu.revision}` : undefined}>
                    {formatDate(menu.updated_at)}
                </div>
            ),
            id: "updated_at",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Обновлено</div>,
        },
        {
            accessorFn: (menu) => <div>{menu.languages.length}</div>,
            id: "languages",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Языки</div>,
        },
        {
            accessorFn: (menu) => (
                <div className="w-full flex justify-end gap-2">
                    <ActionButton
                        onClick={() => menu.id && navigate(`/restaurant-menus/${menu.id}`)}
                        icon={ArrowRightSvg}
                        title="Открыть"
                    />
                    <ActionButton onClick={() => openEditModal(menu)} icon={PencilSvg} title="Редактировать" />
                    <ActionButton onClick={() => setMenuToDelete(menu)} icon={TrashSvg} title="Удалить" />
                </div>
            ),
            id: "actions",
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div>Действия</div>,
        },
    ]

    return (
        <div className="_page_container">
            <PageHeader title="Меню ресторанов" />
            <div className="flexbox">
                {isLoading && (
                    <div className="info_panel">
                        <Loader />
                    </div>
                )}

                {!isLoading && listError && (
                    <div className="info_panel flex items-center justify-between gap-4">
                        <div className="text-color-danger">{listError}</div>
                        <button onClick={fetchMenus} className="form_handle_button !w-auto px-6">
                            Повторить
                        </button>
                    </div>
                )}

                {!isLoading && !listError && isUnavailable && (
                    <div className="info_panel">
                        {unavailableText}
                    </div>
                )}

                {!isLoading && !listError && data && !isUnavailable && (
                    <>
                        <div className="flex items-center justify-between gap-4">
                            <div className="text-lg font-medium">
                                {context.menu_count} из {context.max_menu_count}
                            </div>
                            <span title={isLimitReached ? limitTooltip : undefined}>
                                <button
                                    onClick={openCreateModal}
                                    disabled={isLimitReached}
                                    className="h-[52px] w-fit px-6 text-center bg-color-dark text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Создать меню
                                </button>
                            </span>
                        </div>

                        {menus.length === 0 && (
                            <NoTableData label="Нет данных о меню ресторанов" />
                        )}

                        {menus.length > 0 && (
                            <div className="table_box">
                                <Table columns={columns} data={menus} />
                            </div>
                        )}
                    </>
                )}
            </div>

            <RestaurantMenuModal
                isOpen={isModalOpen}
                currentMenu={currentMenu}
                isSubmitting={isSubmitting}
                serverError={modalError}
                onClose={closeModal}
                onSubmit={handleSubmit}
            />

            <ConfirmModal
                isOpen={Boolean(menuToDelete)}
                title="Удалить меню"
                message={menuToDelete ? `Меню "${menuToDelete.name || menuToDelete.slug || menuToDelete.id}" будет удалено без возможности восстановления. Продолжить?` : ""}
                confirmText="Удалить"
                onConfirm={handleConfirmDelete}
                onCancel={() => setMenuToDelete(null)}
            />
        </div>
    )
}
